/* eslint-disable react-hooks/exhaustive-deps */
import React from 'react';
import { Col } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { ListView, ListViewRow } from 'model/state';
import { Location, typeToString, getRoleFromPermissions, LocationType } from 'model';
import { Link, useParams } from 'react-router-dom';
import { AlertsBadge, FloCheckbox, FloList } from 'components';
import styles from './LocationRolesList.module.css';


type LocationHeaderProps = {
  isSelectedAll:    boolean,
  setSelectAll:     (selectAll: boolean) => void,
};

const LocationHeader: React.FC<LocationHeaderProps> = ({isSelectedAll, setSelectAll}) => {
  const { t } = useTranslation();
  return (
    <>
      <Col xs="5" md="4">
        <FloCheckbox checked={isSelectedAll} onChange={() => setSelectAll(!isSelectedAll)} />
        <span>{t('Name/Address')}</span>
      </Col>
      <Col        md="3" className="d-none d-md-block">{t('City, State, Country')}</Col>
      <Col xs="2" md="2">{t('Type')}</Col>
      <Col xs="3" md="2">{t('Access')}</Col>
      <Col xs="2" md="1">{t('Alerts')}</Col>
    </>
  )
}


type LocationRowProps = {
  location:     ListViewRow<Location>,
  setSelectRow: (selectAll: boolean, locationId: string) => void,
}

const LocationRow: React.FC<LocationRowProps> = React.memo(({location, setSelectRow}) => {
  const { t } = useTranslation();
  let { userId } = useParams();
  const rolKey = getRoleFromPermissions(location.roles || []);
  return (
    <>
      <Col xs="5" md="4" className={styles.firstColumn}>
        <div className={styles.checkboxWrapper}>
          <FloCheckbox className={styles.checkbox} noMargin checked={location.isSelected} onChange={() => setSelectRow(!location.isSelected, location.id || '')} />
        </div>
        <div className={styles.titleWrapper}>
          <div>
            <span>
              { location.type !== LocationType.UNIT ?
                <Link to={`/users/${userId}/roles/${location.id}`}>{location.nickname || location.address}</Link>
                :
                location.nickname || location.address
              }
            </span>
          </div>
          { location.nickname && location.address && <div>{`${location.address}${location.address2 ? `, ${location.address2}` : ''}`}</div> }
        </div>
      </Col>
      <Col        md="3" className="d-none d-md-block">{`${location?.city ? `${location.city}, ` : ''}${location?.state ? `${location?.state.toUpperCase()}, `: '' }${location?.country.toUpperCase()}`}</Col>
      <Col xs="2" md="2">{`${typeToString(location.type)}${location.children.length ? ` (${location.children.length})` : ''}`}</Col>
      <Col xs="3" md="2">{rolKey ? `${t(rolKey)}${location.hasInheritedRolesOnly ? ` (${t('Inherited')})` : ''}` : t('No Access')}</Col>
      <Col xs="2" md="1"><AlertsBadge stats={location.notifications?.pending || { criticalCount: 0, infoCount: 0, warningCount: 0 }} /></Col>
    </>
  )
})


type LocationRolesListProps = {
  locationList:     ListView<Location>,
  isSelectedAll:    boolean,
  setSelectAll:     (selectAll: boolean) => void,
  setSelectRow:     (selectAll: boolean, locationId: string) => void,
}

export const LocationRolesList: React.FC<LocationRolesListProps> = React.memo(({
  locationList,
  isSelectedAll,
  setSelectAll,
  setSelectRow
}) => {
  return (
    <FloList>
      <FloList.Header>
        <LocationHeader isSelectedAll={isSelectedAll} setSelectAll={setSelectAll} />
      </FloList.Header>
      <FloList.Body>
          { locationList.rows?.map(location => <FloList.Row key={location.id}><LocationRow location={location} setSelectRow={setSelectRow} /></FloList.Row>) }
      </FloList.Body>
    </FloList>
  )
})
